import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AssetAccountantSubscription } from '@assetaccountant/api/models/asset-accountant-subscription';
import { PackageType } from '@assetaccountant/api/models/package-type';
import { PricingCalculatorResult } from '@assetaccountant/api/models/pricing-calculator-result';
import { PricingCalculatorRequest } from '@assetaccountant/api/models/pricing-calculator.request';
import { PricingCalculatorApi } from '@assetaccountant/api/services/pricing-calculator.api';
import { getPackageTypeDisplayName } from '@assetaccountant/core/models/enum/package-display';
import { TypedFormBuilder } from '@assetaccountant/core/utils/typed-forms';
import { Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

@Component({
  selector: 'app-subscription-calculator',
  templateUrl: './subscription-calculator.component.html',
  styleUrls: ['./subscription-calculator.component.scss'],
  standalone: false,
})
export class SubscriptionCalculatorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isAccountantPricing = false;
  @Input() packageType?: PackageType;
  @Input() subscription?: AssetAccountantSubscription;

  readonly DEFAULT_ASSETS_PER_REGISTER = 25;

  calculatingPrice = false;

  form;

  previousTotalRegisters = 1;

  pricingResult: PricingCalculatorResult | null | undefined;

  subscriptions: Subscription[] = [];

  get packageDisplayName() {
    return getPackageTypeDisplayName(this.packageType);
  }

  get showCustom() {
    return (
      !(
        this.packageType === PackageType.BusinessLiteV2 ||
        this.packageType === PackageType.AccountingV2Lite
      ) &&
      ((this.form.value.totalRegisters >= 40 && this.isAccountantPricing) ||
        this.form.value.totalAssets >= 900)
    );
  }

  constructor(
    private pricingCalculatorApi: PricingCalculatorApi,
    private fb: TypedFormBuilder,
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      totalRegisters: [this.subscription?.totalRegisters || 1],
      totalAssets: [
        (this.subscription?.totalAssets ?? 0) > this.DEFAULT_ASSETS_PER_REGISTER
          ? this.subscription?.totalAssets
          : this.DEFAULT_ASSETS_PER_REGISTER,
      ],
    });

    this.subscriptions.push(
      ...[
        this.form.valueChanges
          .pipe(tap(() => (this.calculatingPrice = true)))
          .pipe(debounceTime(500))
          .subscribe(values => this.updateTotals(values)),
        this.form?.controls.totalRegisters.valueChanges
          .pipe(debounceTime(500))
          .subscribe(value => this.updateTotalRegisters(value)),
      ],
    );

    this.updateTotals(this.form.value);
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes.packageType || changes.isAccountantPricing) && this.form?.value) {
      this.updateTotals(this.form.value);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  private updateTotalRegisters(totalRegisters: number) {
    if (this.isAccountantPricing && this.form.controls.totalAssets.untouched) {
      this.form.controls.totalAssets.setValue(totalRegisters * this.DEFAULT_ASSETS_PER_REGISTER);
    }
  }

  private updateTotals(values) {
    if (this.subscription?.currency) {
      const request = {
        query: {
          packageType: this.packageType,
          usage: {
            totalRegisters: values.totalRegisters,
            totalAssets: values.totalAssets,
            totalRegistersWithAssets: Math.min(values.totalRegisters, values.totalAssets),
          },
          currency: this.subscription.currency,
        } as PricingCalculatorRequest,
      };

      this.pricingCalculatorApi.getPricingEstimate(request).subscribe(({ result }) => {
        this.pricingResult = result;
        this.calculatingPrice = false;
      });
    }
  }
}
